import { Badge, Box, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { EventValueDto, PriceModelDto } from "../../../autogen/bff-api";
import { priceModelType, usePriceModelDescription } from "../../sourcing-events/common/priceModel";
import { formatEstimatedValue } from "../../sourcing-events/portal/utils/displayFormatters";

interface Props {
  title: string;
  price: EventValueDto | null | undefined;
  points: number | null;
  priceModel: PriceModelDto | null | undefined;
  /** False when the procurement could not be loaded: a null model then means "unknown", not "the
   * default one", and describing the default would state a formula that may not be the published. */
  isPriceModelKnown: boolean;
}

// A price row carries no answer and no input: the tendered price is the answer, and the points come
// straight out of the price formula the procurement was published with.
export const AwardCriterionPriceCard = ({ title, price, points, priceModel, isPriceModelKnown }: Props) => {
  const { t } = useTranslation();
  const describePriceModel = usePriceModelDescription();

  return (
    <Box px={{ base: "4", md: "6" }} py="5">
      <Stack spacing="2">
        <Flex align="center" gap="2">
          <Heading as="h3" size="sm" color="smText">
            {title}
          </Heading>
          <Badge colorScheme="gray" fontSize="2xs">
            {t("auto")}
          </Badge>
        </Flex>
        <Text color="smText">
          {price?.value != null ? formatEstimatedValue(price.value, price.currency) : "–"}
          {" → "}
          <Text as="span" fontWeight="bold" fontFamily="mono">
            {points != null ? points.toFixed(2) : "–"}
          </Text>{" "}
          {t("points")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("Calculated automatically - not scored manually.")}
          {isPriceModelKnown &&
            ` ${describePriceModel(priceModelType(priceModel), priceModel?.hybrid?.breakpointPercent)}`}
        </Text>
      </Stack>
    </Box>
  );
};
